import { useCallback, useEffect, useRef, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'

import { supabase } from '@/lib/supabase/cliente'
import { mensajeDeError } from '@/lib/supabase/errores'
import type { Geocerca } from '@/lib/supabase/tipos-bd'

/**
 * Ubicación: el teléfono se calla mientras no pase nada.
 *
 * La primera versión reportaba cada treinta segundos. Eso gasta batería, llena
 * la tabla de un rastro que nadie va a leer, y guarda muchísimo más de lo que
 * hace falta para saber cuántas horas trabajó alguien.
 *
 * Ahora el teléfono recibe SUS geocercas —una por orden, con centro y radio— y
 * solo habla cuando cruza un borde. Entre entrar y salir puede estar quieto
 * quinientas veces: no agrega nada.
 *
 * LA REGLA SIGUE VIVIENDO EN LA BASE. El teléfono no inventa los radios: los
 * recibe. Aquí solo se usan para decidir CUÁNDO hablar, no QUÉ significa —eso
 * lo sigue decidiendo fn_reportar_posicion—.
 *
 * En Android, esta misma cuenta la hará el sistema operativo con las mismas
 * geocercas, y sin la aplicación abierta.
 */

/**
 * Señal de vida cada diez minutos, aunque no pase nada.
 *
 * Sin esto no habría cómo distinguir «está en el sitio y no se ha movido» de
 * «se le apagó el teléfono», y la alarma de señal perdida no podría existir.
 * Son veinte veces menos tráfico que treinta segundos, y siguen detectando un
 * teléfono caído en menos de media hora.
 */
const LATIDO_MS = 10 * 60 * 1000

export type EstadoRastreo = {
  activo: boolean
  ultimo: { latitud: number; longitud: number; exactitud: number; momento: Date } | null
  ultimoEvento: { evento: string; ordenNumero: string; distancia: number } | null
  error: string | null
}

/** Metros entre dos puntos. La misma cuenta que hace la base, para decidir aquí. */
function distanciaM(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number,
): number {
  const rad = Math.PI / 180
  const a =
    Math.sin(((lat2 - lat1) * rad) / 2) ** 2 +
    Math.cos(lat1 * rad) * Math.cos(lat2 * rad) * Math.sin(((lon2 - lon1) * rad) / 2) ** 2

  return 6_371_000 * 2 * Math.asin(Math.sqrt(a))
}

/** Las áreas de trabajo del colaborador, tal como las define la base. */
export function useGeocercas() {
  const consulta = useQuery({
    queryKey: ['mis-geocercas'],
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<Geocerca[]> => {
      const { data, error } = await supabase.rpc('fn_mis_geocercas')

      if (error) throw new Error(mensajeDeError(error))

      return ((data ?? []) as unknown as Geocerca[]).map((g) => ({
        ...g,
        latitud: Number(g.latitud),
        longitud: Number(g.longitud),
      }))
    },
  })

  return consulta.data ?? []
}

export function useRastreo() {
  const clienteConsultas = useQueryClient()
  const geocercas = useGeocercas()

  const vigilancia = useRef<number | null>(null)
  const ultimoLatido = useRef(0)
  /** En cuáles áreas cree el teléfono que está. Solo habla cuando esto cambia. */
  const adentro = useRef<Set<string>>(new Set())
  const cercasRef = useRef<Geocerca[]>([])
  cercasRef.current = geocercas

  const [estado, setEstado] = useState<EstadoRastreo>({
    activo: false,
    ultimo: null,
    ultimoEvento: null,
    error: null,
  })

  // Al recibir las geocercas se toma como punto de partida lo que la base ya
  // sabe: si está adentro de una, el teléfono no debe anunciar otra entrada.
  useEffect(() => {
    for (const cerca of geocercas) {
      if (cerca.en_sitio) adentro.current.add(cerca.orden_trabajo_id)
    }
  }, [geocercas])

  const reportar = useCallback(
    async (posicion: GeolocationPosition, latido: boolean) => {
      const { latitude, longitude, accuracy } = posicion.coords

      try {
        const { data, error } = await supabase.rpc('fn_reportar_posicion', {
          p_latitud: latitude,
          p_longitud: longitude,
          p_exactitud_m: accuracy,
          p_bateria: null,
          p_origen: 'web',
          p_es_latido: latido,
        })

        if (error) throw new Error(mensajeDeError(error))

        const decidido = (data as unknown as Array<{
          evento: string
          orden_numero: string
          distancia_m: number
        }>)?.[0]

        setEstado((previo) => ({
          ...previo,
          error: null,
          ultimo: {
            latitud: latitude,
            longitud: longitude,
            exactitud: accuracy,
            momento: new Date(),
          },
          ultimoEvento: decidido
            ? {
                evento: decidido.evento,
                ordenNumero: decidido.orden_numero,
                distancia: Number(decidido.distancia_m),
              }
            : previo.ultimoEvento,
        }))

        if (decidido) {
          await Promise.all(
            [['visitas'], ['ordenes-servicio'], ['cronograma'], ['mis-geocercas']].map((clave) =>
              clienteConsultas.invalidateQueries({ queryKey: clave }),
            ),
          )
        }
      } catch (fallo) {
        setEstado((previo) => ({
          ...previo,
          error: fallo instanceof Error ? fallo.message : 'No se pudo reportar la posición.',
        }))
      }
    },
    [clienteConsultas],
  )

  /**
   * Decide si esta posición merece decirse.
   *
   * Cruzar un borde sí. Estar quieto no, salvo que toque el latido.
   */
  const evaluar = useCallback(
    (posicion: GeolocationPosition) => {
      const { latitude, longitude, accuracy } = posicion.coords

      // Un punto con más de doscientos metros de error no sirve para decidir
      // si alguien está en una casa. Se descarta antes de molestar al servidor.
      if (accuracy > 200) return

      let cambio = false

      for (const cerca of cercasRef.current) {
        const metros = distanciaM(latitude, longitude, cerca.latitud, cerca.longitud)
        const estaba = adentro.current.has(cerca.orden_trabajo_id)

        if (!estaba && metros <= cerca.radio_m) {
          adentro.current.add(cerca.orden_trabajo_id)
          cambio = true
        } else if (estaba && metros > cerca.radio_salida_m) {
          adentro.current.delete(cerca.orden_trabajo_id)
          cambio = true
        }
      }

      const ahora = Date.now()
      const tocaLatido = ahora - ultimoLatido.current >= LATIDO_MS

      if (!cambio && !tocaLatido) return

      ultimoLatido.current = ahora
      void reportar(posicion, !cambio)
    },
    [reportar],
  )

  const encender = useCallback(() => {
    if (!('geolocation' in navigator) || vigilancia.current !== null) return

    vigilancia.current = navigator.geolocation.watchPosition(
      evaluar,
      (fallo) =>
        setEstado((previo) => ({
          ...previo,
          error:
            fallo.code === fallo.PERMISSION_DENIED
              ? 'Le negó el permiso de ubicación al navegador.'
              : 'El teléfono no está dando la ubicación.',
        })),
      { enableHighAccuracy: true, timeout: 20_000, maximumAge: 15_000 },
    )

    setEstado((previo) => ({ ...previo, activo: true, error: null }))
  }, [evaluar])

  const apagar = useCallback(() => {
    if (vigilancia.current !== null) {
      navigator.geolocation.clearWatch(vigilancia.current)
      vigilancia.current = null
    }

    setEstado((previo) => ({ ...previo, activo: false }))
  }, [])

  useEffect(() => () => apagar(), [apagar])

  return { estado, encender, apagar, geocercas }
}
